import React from 'react';
import { twMerge } from 'tailwind-merge';

export interface ChipProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The size of the chip */
  size?: 'sm' | 'md' | 'lg';
  /** The visual style variant of the chip */
  variant?: 'solid' | 'soft' | 'outlined';
  /** The color scheme of the chip */
  color?: 'primary' | 'neutral' | 'danger' | 'success' | 'warning' | 'magenta';
  /** Element placed before the children */
  startDecorator?: React.ReactNode;
  /** Element placed after the children */
  endDecorator?: React.ReactNode;
  /** The content of the chip */
  children?: React.ReactNode;
  /** Additional className for the chip */
  className?: string;
  /** Whether the chip is disabled */
  disabled?: boolean;
}

const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
  (
    {
      size = 'md',
      variant = 'solid',
      color = 'neutral',
      startDecorator,
      endDecorator,
      children,
      className = '',
      disabled = false,
      ...props
    },
    ref
  ) => {
    const baseStyles = twMerge(
      'inline-flex items-center justify-center gap-2 rounded-full w-fit transition-all duration-200',
      disabled && 'opacity-50 cursor-default pointer-events-none'
    );

    const sizeStyles = {
      sm: 'min-h-[24px] px-2 py-0.5 text-xs',
      md: 'min-h-[32px] px-3 py-1 text-sm',
      lg: 'min-h-[40px] px-4 py-1.5 text-base',
    };

    const decoratorSizeStyles = {
      sm: 'text-base',
      md: 'text-lg',
      lg: 'text-xl',
    };

    const variantStyles = {
      solid: {
        primary: 'bg-sky-500 text-white',
        neutral: 'bg-neutral-800 text-white',
        danger: 'bg-red-100 text-red-700 border border-red-700',
        success: 'bg-green-600 text-white',
        warning: 'bg-yellow-500 text-white',
        magenta: 'bg-magenta-500 text-white',
      },
      soft: {
        primary: 'bg-sky-50 text-sky-600',
        neutral: 'bg-neutral-100 text-neutral-800',
        danger: 'bg-red-50 text-red-700',
        success: 'bg-green-50 text-green-700',
        warning: 'bg-yellow-50 text-yellow-700',
        magenta: 'bg-magenta-050 text-magenta-700',
      },
      outlined: {
        primary: 'border border-sky-500 text-sky-500',
        neutral: 'border border-neutral-800 text-neutral-800',
        danger: 'border border-red-700 text-red-700',
        success: 'border border-green-600 text-green-600',
        warning: 'border border-yellow-500 text-yellow-500',
        magenta: 'border border-magenta-500 text-magenta-500',
      },
    };

    const chipClasses = twMerge(
      baseStyles,
      sizeStyles[size],
      variantStyles[variant ?? 'solid']?.[color ?? 'neutral'],
      className
    );

    const decoratorClasses = twMerge(
      'flex items-center justify-center',
      decoratorSizeStyles[size]
    );

    return (
      <div ref={ref} className={chipClasses} {...props}>
        {startDecorator && (
          <span className={decoratorClasses}>{startDecorator}</span>
        )}
        <span className="truncate">{children}</span>
        {endDecorator && (
          <span className={decoratorClasses}>{endDecorator}</span>
        )}
      </div>
    );
  }
);

Chip.displayName = 'Chip';

export default Chip;
